import { styled, keyframes } from '@mui/material/styles';
import { Box, Button, IconButton, InputBase, Switch, Typography } from '@mui/material';

import { HEADER_HEIGHT } from '../../../constants/constants';

// 필수 입력 누락 시 빨간 펄스 glow 애니메이션
const errorPulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
`;

// 계약서 만들기 - Step1(계약서 정보) styled (Figma 5154:8692)
// Primary #4AB3BC · 라벨 #141414 · placeholder/보더 #A6A6A6 · 카드보더 #D9D9D9
// 안내박스 #F2F2F2 · 불러오기 버튼 #333 · 취소 #A6A6A6

// ============================================
// 레이아웃
// ============================================
export const CreateContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
  marginTop: '16px',
  padding: '32px',
  background: '#fafbfc',
  borderRadius: '8px',
  boxShadow: '0px 0px 2px rgba(0,0,0,0.16)',
});

export const TopRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

// 목록으로
export const BackGroup = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  cursor: 'pointer',
});

export const BackIconButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: 0,
  background: '#262626',
  borderRadius: '12px',
  color: '#fff',
  '&:hover': { background: '#262626' },
  '& svg': { fontSize: 18 },
});

export const BackText = styled(Typography)({
  fontSize: '20px',
  fontWeight: 500,
  color: '#141414',
});

export const BodyRow = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  width: '100%',
});

export const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  width: '360px',
  flexShrink: 0,
  // 오른쪽 약관내용이 길어 스크롤될 때, 왼쪽 기본정보 카드가 따라오도록 상단 고정
  // 고정 헤더(HEADER_HEIGHT)에 가려지지 않도록 그 아래 16px 지점에 붙인다.
  position: 'sticky',
  top: `${HEADER_HEIGHT + 16}px`,
  alignSelf: 'flex-start',
});

export const RightColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
});

// ============================================
// 스텝 인디케이터 (브레드크럼 화살표)
// ============================================
export const StepRow = styled(Box)({
  display: 'flex',
});

export const StepTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'first',
})(({ active, first }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  height: '48px',
  paddingLeft: first ? '16px' : '32px',
  paddingRight: '40px',
  marginRight: first ? '-18px' : 0,
  background: active ? '#4AB3BC' : '#F2F2F2',
  // 브레드크럼 화살표 모양 (오른쪽 꼭지 + 첫 탭이 아니면 왼쪽 노치)
  clipPath: first
    ? 'polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)'
    : 'polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%, 18px 50%)',
}));

export const StepBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '15px',
  flexShrink: 0,
  background: active ? '#fff' : '#A6A6A6',
  color: active ? '#4AB3BC' : '#fff',
  fontSize: '18px',
  fontWeight: 600,
}));

export const StepLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: active ? '#fff' : '#737373',
  whiteSpace: 'nowrap',
}));

// ============================================
// 카드 / 필드 공통
// ============================================
export const Card = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
  padding: '24px',
  background: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

export const CardTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#4AB3BC',
});

export const FieldList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
});

export const Field = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const FieldLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const FieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
});

// 입력칸 (텍스트)
export const TextField = styled(InputBase, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  height: '44px',
  padding: '12px 16px',
  background: '#fff',
  border: hasError ? '2px solid #dc2626' : '1px solid #A6A6A6',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#141414',
  '& .MuiInputBase-input': { padding: '0 !important' },
  '& input::placeholder': { color: '#A6A6A6', opacity: 1 },
  ...(hasError && { animation: `${errorPulseGlow} 1.5s ease-in-out infinite` }),
}));

// 셀렉트 모양 박스 (placeholder + 화살표) — 퍼블리싱용 표시 전용
export const SelectBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled' && prop !== 'hasError',
})(({ disabled, hasError }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  height: '44px',
  padding: '12px 16px',
  background: disabled ? '#f5f5f5' : '#fff',
  border: hasError ? '2px solid #dc2626' : '1px solid #A6A6A6',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  ...(hasError && { animation: `${errorPulseGlow} 1.5s ease-in-out infinite` }),
}));

export const Placeholder = styled(Typography)({
  flex: 1,
  minWidth: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: '#A6A6A6',
});

// 직인/로고 드롭다운 옵션 안의 미리보기 썸네일
export const OptionThumb = styled('img')({
  width: '28px',
  height: '28px',
  marginRight: '8px',
  objectFit: 'contain',
  border: '1px solid #eee',
  borderRadius: '4px',
});

// 작은 토글 (32x16)
export const MiniToggle = styled(Switch)({
  width: 32,
  height: 16,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1.6,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 12.8, height: 12.8, boxShadow: 'none' },
  '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#D9D9D9', opacity: 1 },
});

// ============================================
// 안내 박스
// ============================================
export const InfoBox = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  width: '100%',
  padding: '16px 24px',
  background: '#F2F2F2',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
});

export const InfoText = styled(Typography)({
  flex: 1,
  minWidth: 0,
  fontSize: '12px',
  fontWeight: 500,
  color: '#737373',
  lineHeight: 1.5,
});

// ============================================
// 약관 내용 (textarea + 불러오기)
// ============================================
export const TermHeaderRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  width: '100%',
});

export const TermLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#141414',
});

export const TermBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled' && prop !== 'hasError',
})(({ disabled, hasError }) => ({
  position: 'relative',
  height: '120px',
  padding: '12px 16px',
  background: disabled ? '#f5f5f5' : '#fff',
  border: hasError ? '2px solid #dc2626' : '1px solid #A6A6A6',
  borderRadius: '4px',
  overflow: 'hidden',
  ...(hasError && { animation: `${errorPulseGlow} 1.5s ease-in-out infinite` }),
}));

export const TermInput = styled(InputBase)({
  width: '100%',
  height: '100%',
  alignItems: 'flex-start',
  padding: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: '#141414',
  // textarea 가 내용에 따라 자라면서 박스를 벗어나지 않도록 박스 높이에 고정하고 스크롤시킨다.
  // 하단 28px 은 불러오기 버튼이 글자 위에 겹치지 않도록 비워둔다.
  '& textarea': {
    height: '100% !important',
    overflow: 'auto !important',
    paddingBottom: '28px',
    boxSizing: 'border-box',
  },
  '& textarea::placeholder': { color: '#A6A6A6', opacity: 1 },
  // 비활성(토글 OFF) 시 입력 불가 표시
  '& textarea:disabled': {
    WebkitTextFillColor: '#A6A6A6',
    cursor: 'not-allowed',
  },
});

// TermBox 안쪽 우측 하단에 고정 (textarea 위에 떠 있음)
export const LoadButton = styled(Button)({
  position: 'absolute',
  right: '16px',
  bottom: '12px',
  minWidth: 'auto',
  padding: '6px 16px',
  borderRadius: '4px',
  background: '#333',
  border: '1px solid #333',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#333', boxShadow: 'none' },
  // 비활성(토글 OFF) 시 회색 처리
  '&.Mui-disabled': { background: '#bdbdbd', borderColor: '#bdbdbd', color: '#fff' },
});

// ============================================
// 푸터 버튼
// ============================================
export const FooterRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
});

export const CancelButton = styled(Button)({
  width: '100px',
  height: '48px',
  borderRadius: '4px',
  background: '#A6A6A6',
  color: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#9a9a9a', boxShadow: 'none' },
});

export const NextButton = styled(Button)({
  width: '240px',
  height: '48px',
  borderRadius: '4px',
  background: '#4AB3BC',
  color: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { background: '#4AB3BC', boxShadow: 'none' },
});
